// Persists per-message thumbs feedback so the selected vote survives page reloads.
//
// Feedback is sent as an outbound custom notification and is not returned in message
// history, so without this the solid green/red thumb state would be lost on refresh.
// Keys are the bot message id (always present on received messages). Mirrors
// validation-persistence: try/catch degrade when storage is unavailable, and keys
// are scoped per session so distinct conversations never collide.

const KEY_PREFIX = 'amio_chat_message_feedback'
const MAX_FEEDBACK_ENTRIES = 100

function getStorage (storageType) {
  try {
    return storageType === 'session' ? window.sessionStorage : window.localStorage
  } catch (e) {
    return null
  }
}

function keyFor (sessionId) {
  return sessionId ? `${KEY_PREFIX}_${sessionId}` : KEY_PREFIX
}

function readMap (storageType, sessionId) {
  const storage = getStorage(storageType)
  if (!storage) return {}
  try {
    const raw = storage.getItem(keyFor(sessionId))
    if (!raw) return {}
    const parsed = JSON.parse(raw)
    return parsed && typeof parsed === 'object' ? parsed : {}
  } catch (e) {
    return {}
  }
}

function writeMap (storageType, sessionId, map) {
  const storage = getStorage(storageType)
  if (!storage) return
  try {
    storage.setItem(keyFor(sessionId), JSON.stringify(map))
  } catch (e) { /* storage full/unavailable - degrade silently */ }
}

function trimMap (map) {
  const keys = Object.keys(map)
  if (keys.length <= MAX_FEEDBACK_ENTRIES) return map

  const trimmed = {}
  for (const key of keys.slice(-MAX_FEEDBACK_ENTRIES)) {
    trimmed[key] = map[key]
  }
  return trimmed
}

export function clearStaleFeedbackSessions (storageType, currentSessionId) {
  const storage = getStorage(storageType)
  if (!storage || !currentSessionId) return

  const keepKey = keyFor(currentSessionId)
  try {
    for (let i = storage.length - 1; i >= 0; i--) {
      const key = storage.key(i)
      if (key && key.startsWith(`${KEY_PREFIX}_`) && key !== keepKey) {
        storage.removeItem(key)
      }
    }
  } catch (e) { /* degrade silently */ }
}

export function getFeedback (storageType, sessionId, messageId) {
  if (!messageId) return null
  const map = readMap(storageType, sessionId)
  return map[String(messageId)] || null
}

export function saveFeedback (storageType, sessionId, messageId, value) {
  if (!messageId || !sessionId) return
  const map = readMap(storageType, sessionId)
  const key = String(messageId)
  if (key in map) delete map[key]
  map[key] = value
  writeMap(storageType, sessionId, trimMap(map))
}

export function clearFeedback (storageType, sessionId, messageId) {
  if (!messageId || !sessionId) return
  const map = readMap(storageType, sessionId)
  const key = String(messageId)
  if (!(key in map)) return
  delete map[key]
  writeMap(storageType, sessionId, map)
}
